// v0.5.9 Q4 — Per-row resolver for tier-3 refunds flagged
// REFUND_CHANNEL_MANUAL_ASSIGNMENT_REQUIRED. Auto-pairing could not
// determine which original sale a credit note reverses, so the operator
// picks the correct channel + records a reason. Mirrors the
// ServiceCountryResolver pattern: the backend records the override signal
// + an audit decision, and the next re-run has refund_pairing honour it.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { assignRefundChannel } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { REFUND_CHANNEL_REASONS } from "../../lib/reviewReasonPresets";

// v0.6.9 — operator feedback (screenshot): "why are there two identical
// options here?" REFUND_B2B/REFUND_B2C used to both read "National refund
// — B2? (§17)", differing only by two characters buried in the middle of
// the string — easy to pick the wrong one at a glance in a <select>, which
// this codebase's own convention (see FULFILMENT_QUICK_REF in
// api/routes_client_template.py) treats as worth fixing on sight, not just
// when it causes a wrong answer. The distinguishing word now leads each
// label instead of trailing it. Also added REVERSE_CHARGE, which had no
// entry at all and rendered as the bare enum string via the `|| o` fallback
// below — the second thing flagged in the same screenshot.
export const CHANNEL_LABELS = {
  DISTANCE_SALE: "Union OSS — distance sale (Art 33)",
  OSS_SERVICE: "Union OSS — digital service (Art 58)",
  EU_NON_UNION_OSS: "Non-Union OSS",
  IOSS_SCHEME: "IOSS — import scheme",
  // A2-588 lead 9: a credit note for a sale the marketplace is deemed to make
  MARKETPLACE: "Marketplace — deemed supplier (Art 14a)",
  REFUND_B2B: "B2B customer — national refund (§17)",
  REFUND_B2C: "B2C customer — national refund (§17)",
  // v0.6.9 — added alongside the REVERSE_CHARGE refund-netting fix
  // (engine/refund_pairing.py): a refund against a reverse-charged
  // sale needs its own channel, since the seller charged no output
  // VAT on the original (the buyer self-accounted it) and this
  // credit note must stay out of the national refund columns above.
  REVERSE_CHARGE: "Reverse charge — buyer self-accounts (Art 194)",
};

// A2-532 — `tids` assigns one channel to several credit notes at once (the
// card's bulk bar); failures listed by row; the next re-run applies them all.
export const RefundChannelResolver = ({
  tid, tids, calcId, calcFrozen, acked, options = [], active = [], onResolved,
  hideChip = false,
}) => {
  const { t } = useTranslation();
  const ids = tids && tids.length ? tids : [tid];
  const key = tids ? "bulk" : tid;   // test ids
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => { setChannel(""); setReason(""); setPreset(""); setErr(null); };

  const cleaned = reason.trim();
  const valid = !!channel && cleaned.length >= 10 && !/[<>]/.test(cleaned);

  // Order the picker: active channels first (order activity in this period),
  // then the rest. Never shrink the allowed set — inactive channels stay
  // selectable, just visually demoted with a hint.
  const activeSet = new Set(active);
  const ordered = [
    ...options.filter((o) => activeSet.has(o)),
    ...options.filter((o) => !activeSet.has(o)),
  ];

  if (acked) {
    return (
      <>
      {hideChip ? (
        <span data-testid={`rg-done-${tid}`} data-resolved="true" className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t("ui.review_grouped.row_resolved_label", { defaultValue: "Reviewed" })}
        </span>
      ) : (
      <span
        data-testid={`rg-txn-${tid}`}
        data-resolved="true"
        className="px-2 py-1 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200 truncate text-[10px] tabular-nums"
        title={tid}
      >
        ✓ {tid.slice(0, 12)}…
      </span>
      )}
      </>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      const failed = [];
      for (const id of ids) {
        try {
          await assignRefundChannel(calcId, { txn_id: id, channel, reason: cleaned });
        } catch (e) {
          if (ids.length === 1) throw e;
          failed.push(`${id.slice(0, 12)}…: ${errorMessage(e)}`);
        }
      }
      if (failed.length === ids.length) throw new Error(failed.join("; "));
      setOpen(false); reset();
      toast.success(
        t("ui.review_grouped.rc_toast_ok", { defaultValue: "Channel assigned — figures change at the next re-run" }),
        { description: ids.length === 1 ? `${ids[0].slice(0, 12)}… → ${channel}`
          : t("ui.review_grouped.rc_toast_ok_bulk", {
            n: ids.length - failed.length, channel,
            defaultValue: `${ids.length - failed.length} credit notes → ${channel}`,
          }) },
      );
      if (failed.length) {
        toast.error(t("ui.review_grouped.rc_toast_some_failed", {
          n: failed.length, defaultValue: `${failed.length} could not take that channel` }),
        { description: failed.slice(0, 5).join("\n") });
      }
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(
        t("ui.review_grouped.rc_toast_fail", { defaultValue: "Could not assign channel" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className="flex items-center gap-2 flex-wrap">
      {!hideChip && (
      <span
        data-testid={`rg-txn-${tid}`}
        data-resolved="false"
        className="px-2 py-1 rounded border bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 truncate text-[10px] tabular-nums"
        title={tid}
      >
        {tid.slice(0, 12)}…
      </span>
      )}
      {!calcFrozen && (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`rc-resolve-open-${key}`}
              className="text-[10px] tabular-nums px-2 py-1 rounded border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-200"
            >
              {tids
                ? t("ui.review_grouped.rc_resolve_btn_bulk", { n: ids.length, defaultValue: `Assign channel to ${ids.length} selected →` })
                : t("ui.review_grouped.rc_resolve_btn", { defaultValue: "Assign channel →" })}
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid={`rc-resolve-popover-${key}`}
            className="w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
          >
            <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
              {tids
                ? t("ui.review_grouped.rc_resolve_title_bulk", { n: ids.length, defaultValue: `Which channel do these ${ids.length} credit notes belong to?` })
                : t("ui.review_grouped.rc_resolve_title", { defaultValue: "Which channel does this refund belong to?" })}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
              {t("ui.review_grouped.rc_resolve_help", {
                defaultValue:
                  "Auto-pairing couldn't find the original sale this credit note reverses. Pick the channel of the original supply so the refund nets on the correct return.",
              })}
            </div>
            <select
              data-testid={`rc-resolve-channel-${key}`}
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs"
            >
              <option value="">
                {t("ui.review_grouped.rc_channel_placeholder", { defaultValue: "Select a channel…" })}
              </option>
              {ordered.map((o) => (
                <option key={o} value={o}>
                  {(CHANNEL_LABELS[o] || o)}
                  {activeSet.has(o) ? t("ui.review_grouped.refund_channel_active", { defaultValue: " · active this period" }) : ""}
                </option>
              ))}
            </select>
            {/* v0.6.12 A5-27 — pick, don't write. */}
            <PresetReasonSelect
              testid={`rc-resolve-reason-preset-${key}`}
              presets={REFUND_CHANNEL_REASONS}
              preset={preset}
              onPick={(p, v) => { setPreset(p); setReason(v); }}
            />
            {err && (
              <div data-testid={`rc-resolve-error-${key}`} className="text-[10px] text-red-700 dark:text-red-300">
                {err}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid={`rc-resolve-cancel-${key}`}
                onClick={() => { setOpen(false); reset(); }}
                className="text-[10px] underline text-slate-600 dark:text-slate-300"
              >
                {t("ui.review_grouped.rc_cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                data-testid={`rc-resolve-submit-${key}`}
                data-resolution-class="self-resolve"
                data-resolution-endpoint="POST /api/runs/calc/{calc_id}/assign-refund-channel"
                disabled={busy || !valid}
                onClick={submit}
                className="text-[10px] font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
              >
                {busy
                  ? t("ui.review_grouped.rc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_grouped.rc_submit", { defaultValue: "Save" })}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
    </>
  );
};

export default RefundChannelResolver;
